import { useState, useCallback } from 'react';
import { rwColors } from '@/styles/colors';
import { LoaderScreen } from '@/components/Elements/LoaderScreen';
import { PlusCircleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';

import { BaseSlider } from '@/features/connect-dots/components/Slider';
import { NetworkContainer } from '@/features/connect-dots/components/visualizer/NetworkContainer';
import {
  MIN_ZOOM,
  MAX_ZOOM,
  ZOOM_STEP,
} from '@/features/connect-dots/types/cytoscape';
import { useNetworkState } from '@/features/connect-dots/hooks/useNetworkState';
import { useNetworkElements } from '@/features/connect-dots/hooks/useNetworkElements';

import '@/features/connect-dots/styles/connectDots.css';

export const TagViewContainer = ({ isOpen }: { isOpen: boolean }) => {
  // ズーム関連のstateとハンドラを追加
  const [zoomLevel, setZoomLevel] = useState(0.5);
  // page内共通のstateとハンドラ
  const { cytoscapeCoreRefObject, setCytoscapeCoreRefObject } =
    useNetworkState();

  const {
    elements,
    displayedData,
    nodeTypeFilters,
    updateNodeTypeFilter,
    isFilteringCompleted,
    networkError,
  } = useNetworkElements();

  const handleZoomChange = useCallback(
    (value: number) => {
      if (!cytoscapeCoreRefObject) return;
      cytoscapeCoreRefObject.zoom(value);
    },
    [cytoscapeCoreRefObject]
  );

  // cytoscape の zoom イベントと初期値同期の useEffect を削除（NetworkContainer に移動）

  // ★ エラーハンドリング
  if (networkError) {
    console.error('Network data error:', networkError);
  }

  if (!isFilteringCompleted) {
    return <LoaderScreen contained={true} />;
  }

  return (
    <div className="relative size-full min-h-0">
      {/* ネットワーク図 */}
      <div className="relative flex h-full min-h-[300px]">
        <NetworkContainer
          elements={elements}
          isFilteringCompleted={isFilteringCompleted}
          filteredData={displayedData}
          isOpen={isOpen}
          cytoscapeCoreRefObject={cytoscapeCoreRefObject}
          setCytoscapeCoreRefObject={setCytoscapeCoreRefObject}
          zoomLevel={zoomLevel}
          setZoomLevel={setZoomLevel}
          nodeTypeFilters={nodeTypeFilters}
          updateNodeTypeFilter={updateNodeTypeFilter}
        />
        <div
          className="absolute right-4 top-2/3 z-20 -translate-y-1/2"
          style={{ borderRadius: '50%' }}
        >
          <BaseSlider
            value={zoomLevel}
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={ZOOM_STEP}
            label1={
              <button
                type="button"
                className="cursor-pointer focus:outline-none"
                onClick={() => {
                  if (cytoscapeCoreRefObject) {
                    cytoscapeCoreRefObject.zoom(
                      Math.min(
                        cytoscapeCoreRefObject.zoom() + ZOOM_STEP,
                        MAX_ZOOM
                      )
                    );
                  }
                }}
                tabIndex={0}
              >
                <PlusCircleIcon
                  className="size-6 stroke-white"
                  style={{ fill: rwColors['dark-gray'] }}
                />
              </button>
            }
            label2={
              <button
                type="button"
                className="cursor-pointer focus:outline-none"
                onClick={() => {
                  if (cytoscapeCoreRefObject) {
                    cytoscapeCoreRefObject.zoom(
                      Math.max(
                        cytoscapeCoreRefObject.zoom() - ZOOM_STEP,
                        MIN_ZOOM
                      )
                    );
                  }
                }}
                tabIndex={0}
              >
                <MinusCircleIcon
                  className="size-6 stroke-white"
                  style={{ fill: rwColors['dark-gray'] }}
                />
              </button>
            }
            onChange={handleZoomChange}
            vertical={true}
            trackColor={rwColors.gray}
            fillColor={rwColors.main}
            thumbColor={rwColors.sub}
            className="rounded-lg bg-white/10 p-1 shadow-lg backdrop-blur-sm"
          />
        </div>
      </div>
    </div>
  );
};
